/**
 * Vorschau langer Textfelder im Kundeneditor.
 *
 * Zugeklappte Textfelder zeigen statt des ganzen Editors nur den Anfang ihres
 * Inhalts. Die Aufbereitung kommt bewusst ohne DOM aus: Sie läuft im Browser
 * und im Node-Test gleich, und ein Text aus der Kampagne wird dabei nie als
 * HTML ausgewertet.
 *
 *  - HTML wird zu Text: Tags fallen weg, Zeilen- und Absatzenden werden zu
 *    Leerzeichen, damit Wörter nicht zusammenkleben
 *  - Leerraum wird zusammengefasst, Zeilenumbrüche eingeschlossen
 *  - Die Länge ist begrenzt. Das CSS schneidet ohnehin nach zwei Zeilen ab,
 *    die Kürzung hier sorgt dafür, dass kein Roman im DOM steht
 *
 * @see resources/js/components/editor-fields/CollapsibleText.vue
 * @see resources/js/tests/textPreview.test.mjs
 */

/** Etwa zwei Zeilen eines Feldes in voller Formularbreite. */
export const PREVIEW_MAX_LENGTH = 180;

export const ELLIPSIS = '…';

/*
 * Benannte Entities, die in Feldinhalten tatsächlich vorkommen. TipTap selbst
 * schreibt nur &amp; &lt; &gt; &nbsp;, ältere Inhalte aus dem JSON-Editor
 * aber auch Umlaute und Satzzeichen als Entity.
 */
const NAMED_ENTITIES = {
    amp: '&',
    lt: '<',
    gt: '>',
    quot: '"',
    apos: "'",
    nbsp: ' ',
    shy: '',
    hellip: '…',
    ndash: '–',
    mdash: '—',
    bdquo: '„',
    ldquo: '“',
    rdquo: '”',
    sbquo: '‚',
    lsquo: '‘',
    rsquo: '’',
    laquo: '«',
    raquo: '»',
    euro: '€',
    copy: '©',
    reg: '®',
    trade: '™',
    auml: 'ä',
    ouml: 'ö',
    uuml: 'ü',
    Auml: 'Ä',
    Ouml: 'Ö',
    Uuml: 'Ü',
    szlig: 'ß',
};

// Ende eines Blocks oder ein Umbruch trennt Wörter.
const WORD_BREAKS = /<br\s*\/?>|<hr\s*\/?>|<\/(?:p|li|h[1-6]|div|blockquote|pre|tr|td|th|ul|ol|dt|dd)\s*>/gi;

// Ein echtes Tag beginnt mit einem Buchstaben. Attributwerte in Anführungszeichen
// dürfen ">" enthalten. Ein einzelnes "<" im Text ("3 < 4") bleibt stehen.
const TAG = /<\/?[a-z][a-z0-9-]*(?:[^>"']|"[^"]*"|'[^']*')*>/gi;

const ENTITY = /&(#[xX][0-9a-fA-F]+|#[0-9]+|[a-zA-Z][a-zA-Z0-9]*);/g;

function isValidCodePoint(code) {
    return Number.isInteger(code)
        && code > 0
        && code <= 0x10FFFF
        && !(code >= 0xD800 && code <= 0xDFFF);
}

/**
 * Entities in einem Durchgang dekodieren.
 *
 * Ein Durchgang, damit "&amp;lt;" zu "&lt;" wird und nicht zu "<".
 * Unbekannte Entities bleiben, wie sie sind.
 *
 * @param {string} text
 * @returns {string}
 */
export function decodeEntities(text) {
    return String(text ?? '').replace(ENTITY, (match, name) => {
        if (name[0] === '#') {
            const hex = name[1] === 'x' || name[1] === 'X';
            const code = hex ? parseInt(name.slice(2), 16) : parseInt(name.slice(1), 10);

            return isValidCodePoint(code) ? String.fromCodePoint(code) : match;
        }

        return Object.prototype.hasOwnProperty.call(NAMED_ENTITIES, name) ? NAMED_ENTITIES[name] : match;
    });
}

/**
 * HTML zu lesbarem Text, ohne DOM.
 *
 * @param {string|null|undefined} html
 * @returns {string}
 */
export function htmlToText(html) {
    if (html === null || html === undefined) return '';

    const withoutTags = String(html)
        .replace(/<!--[\s\S]*?-->/g, ' ')
        .replace(/<(script|style)\b[^>]*>[\s\S]*?<\/\1\s*>/gi, ' ')
        .replace(WORD_BREAKS, ' ')
        .replace(TAG, '');

    // Erst nach dem Entfernen der Tags dekodieren: "&lt;b&gt;" ist Text.
    return decodeEntities(withoutTags);
}

/**
 * Jeder Leerraum (auch Zeilenumbruch, Tab, geschütztes Leerzeichen) wird zu
 * einem einfachen Leerzeichen, Anfang und Ende werden abgeschnitten.
 *
 * @param {string|null|undefined} text
 * @returns {string}
 */
export function collapseWhitespace(text) {
    return String(text ?? '').replace(/\s+/g, ' ').trim();
}

/**
 * Text auf höchstens maxLength Zeichen kürzen, Auslassungszeichen inklusive.
 *
 * Gezählt wird in Unicode-Zeichen, damit kein Emoji halbiert wird. Gekürzt
 * wird am Wortende, solange dabei nicht mehr als 40 Prozent verloren gehen.
 *
 * @param {string} text
 * @param {number} maxLength
 * @param {string} ellipsis
 * @returns {string}
 */
export function truncateText(text, maxLength = PREVIEW_MAX_LENGTH, ellipsis = ELLIPSIS) {
    const value = String(text ?? '');
    if (!Number.isFinite(maxLength)) return value;

    const limit = Math.max(1, Math.floor(maxLength));
    const chars = Array.from(value);
    if (chars.length <= limit) return value;

    const room = limit - Array.from(ellipsis).length;
    if (room < 1) return Array.from(ellipsis).slice(0, limit).join('');

    let cut = chars.slice(0, room).join('');

    // Endet der Schnitt nicht ohnehin an einer Wortgrenze, zurück zum letzten Leerzeichen.
    if (!/\s/.test(chars[room] ?? '')) {
        const lastSpace = cut.lastIndexOf(' ');
        if (lastSpace >= Math.floor(room * 0.6)) cut = cut.slice(0, lastSpace);
    }

    // Kein "Wort,…" und kein "Wort …".
    cut = cut.replace(/[\s.,;:!?\-–—]+$/u, '');

    return (cut === '' ? chars.slice(0, room).join('') : cut) + ellipsis;
}

/**
 * Vorschau eines Feldwerts: aufbereitet, zusammengefasst, gekürzt.
 *
 * Nur Texte und Zahlen haben eine Vorschau. Alles andere (null, Listen,
 * Objekte) ergibt einen leeren Text, das Feld gilt dann als leer.
 *
 * @param {*} value
 * @param {{html?: boolean, maxLength?: number}} options
 *        html: Der Wert ist HTML (RichText). Ohne html bleibt ein Tag im Text
 *        stehen, so wie ihn das Textfeld auch zeigt.
 * @returns {string}
 */
export function textPreview(value, {html = false, maxLength = PREVIEW_MAX_LENGTH} = {}) {
    if (typeof value !== 'string' && typeof value !== 'number') return '';

    const raw = String(value);
    const text = collapseWhitespace(html ? htmlToText(raw) : raw);

    return truncateText(text, maxLength);
}
